import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@/shared/services/notifications.service';
import { isSafeNotificationLink } from '@/shared/lib/notification-link';
import { useWorkspace } from './useWorkspace';
import { useMarkNotificationRead } from './useNotifications';

/**
 * Opens a notification: marks it read, switches to the brand it is from
 * when that is not the brand in view (an agency's Brand A failure opened
 * under Brand B showed B's data, N05), then follows its link when the link
 * is a known in-app route.
 */
export function useOpenNotification(): (n: Notification) => void {
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspace();
  return useCallback(
    (n: Notification) => {
      if (!n.read) markRead.mutate(n.id);
      if (n.workspaceId && n.workspaceId !== currentWorkspace?.id) {
        const target = workspaces.find((w) => w.id === n.workspaceId);
        if (target) switchWorkspace(target);
      }
      if (isSafeNotificationLink(n.link)) navigate(n.link);
    },
    [markRead, workspaces, currentWorkspace?.id, switchWorkspace, navigate],
  );
}
